{
  const AB_CLASS = 'ab-free-shipping-119';
  const CONTROL_LIMIT = 99;
  const VARIANT_LIMIT = 119;
  const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION', 'IFRAME', 'SVG']);
  const PRICE_SELECTOR = [
    '.price',
    '[data-price]',
    '[data-product-price]',
    '[data-cart-item-price]',
    '[data-left-to-spend]',
    '.cart-item__price',
    'product-price',
    '.money',
  ].join(', ');
  const AMOUNT_RE = /\$99(?:\.00)?(?!\.\d)(\+)?/g;
  const THRESHOLD_COPY_RE = /((?:over|on orders|orders|spend over)\s+)\$\s*[\d,.]+(?:\s*USD)?(\+)?/gi;
  const SHIPPING_CONTEXT_RE =
    /free\s*shipping|free on orders|orders\s+(?:over\s+)?\$?99|spend over\s*\$?99|env[ií]o gratis|livraison gratuite|kostenloser versand|spedizione gratuita|frete gr[aá]tis|envio gratuito/i;
  const htmlOriginals = new WeakMap();
  const copyOriginals = new WeakMap();
  let didApply = false;

  const displayedCartTotal = () => document.querySelector('[data-cart-total]');

  const displayCurrency = () => {
    const cartCurrency = displayedCartTotal()?.textContent?.match(/\b[A-Z]{3}\b/)?.[0];
    return cartCurrency || window.Shopify?.currency?.active || 'USD';
  };

  const variantShopCents = () => VARIANT_LIMIT * 100;

  const variantPresentmentCents = variantShopCents;

  const cartShopCents = () => {
    return cartSubtotalCents();
  };

  const formatShopMoney = (shopCents) => {
    const presentmentValue = Math.max(0, Number(shopCents) || 0) / 100;
    const currency = displayCurrency();

    try {
      return new Intl.NumberFormat(window.Shopify?.locale || document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        currencyDisplay: currency === 'USD' ? 'narrowSymbol' : 'symbol',
        minimumFractionDigits: Number.isInteger(presentmentValue) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(presentmentValue);
    } catch (_error) {
      const formatted = presentmentValue % 1 === 0 ? String(presentmentValue) : presentmentValue.toFixed(2);
      return currency === 'USD' ? `$${formatted}` : `${formatted} ${currency}`;
    }
  };

  const isActive = () => document.documentElement.classList.contains(AB_CLASS);

  const parseDisplayedMoneyCents = (text) => {
    const compact = String(text || '')
      .replace(/[^\d.,-]/g, '')
      .replace(/\s/g, '');
    if (!compact) return null;

    const lastDot = compact.lastIndexOf('.');
    const lastComma = compact.lastIndexOf(',');
    const decimalIndex = Math.max(lastDot, lastComma);
    const decimalDigits = decimalIndex >= 0 ? compact.length - decimalIndex - 1 : 0;
    const hasDecimal = decimalIndex >= 0 && decimalDigits > 0 && decimalDigits <= 2;
    const integerPart = hasDecimal ? compact.slice(0, decimalIndex) : compact;
    const fractionPart = hasDecimal ? compact.slice(decimalIndex + 1) : '';
    const normalized = `${integerPart.replace(/[^\d-]/g, '')}.${fractionPart || '0'}`;
    const amount = Number(normalized);
    return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : null;
  };

  const cartSubtotalCents = () => {
    const displayedTotal = parseDisplayedMoneyCents(displayedCartTotal()?.textContent);
    if (displayedTotal != null) return displayedTotal;

    const cartComponent = Array.from(document.querySelectorAll('cart-items')).find((cart) => {
      const subtotal = Number(cart.subtotal);
      return Number.isFinite(subtotal) && subtotal >= 0;
    });
    if (cartComponent) return Number(cartComponent.subtotal);

    const progress = document.querySelector('[data-cart-progress]');
    const fromProgress = Number(progress?.getAttribute('data-progress-cart-total'));
    if (Number.isFinite(fromProgress) && fromProgress >= 0) return fromProgress;
    return Number(window.theme?.subtotal || 0);
  };

  const isFreeShippingContext = (text) => SHIPPING_CONTEXT_RE.test(text || '');

  const createAmountNode = (shopCents, plus = '') => {
    // Never use .trans-money here. Transtore treats the number as presentment
    // (119 MKD ≈ $3 USD) or round-trips USD→FX→USD ($121).
    return document.createTextNode(`${formatShopMoney(shopCents)}${plus}`);
  };

  const hasCopyAmount = (text) => {
    const value = text || '';
    const hasNinetyNine = AMOUNT_RE.test(value) || /(^|[^\d])99\+/.test(value);
    AMOUNT_RE.lastIndex = 0;
    THRESHOLD_COPY_RE.lastIndex = 0;
    return hasNinetyNine || THRESHOLD_COPY_RE.test(value);
  };

  const themeLimitDollars = () => {
    const first = document.querySelector('[data-free-shipping]');
    const parsed = Number(first?.getAttribute('data-free-shipping-limit'));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : CONTROL_LIMIT;
  };

  const fragmentFromCopy = (source) => {
    const frag = document.createDocumentFragment();
    const trimmed = source.trim();
    const bareMoney = trimmed.match(/^\$\s*[\d,.]+(?:\s*USD)?(\+)?$/);

    if (bareMoney) {
      frag.append(createAmountNode(variantShopCents(), bareMoney[1] || ''));
      return frag;
    }

    let lastIndex = 0;
    THRESHOLD_COPY_RE.lastIndex = 0;
    let match = THRESHOLD_COPY_RE.exec(source);

    while (match) {
      if (match.index > lastIndex) {
        frag.append(source.slice(lastIndex, match.index));
      }
      frag.append(match[1]);
      frag.append(createAmountNode(variantShopCents(), match[2] || ''));
      lastIndex = match.index + match[0].length;
      match = THRESHOLD_COPY_RE.exec(source);
    }

    if (lastIndex === 0) {
      const re = new RegExp(AMOUNT_RE.source, 'g');
      match = re.exec(source);
      while (match) {
        if (match.index > lastIndex) {
          frag.append(source.slice(lastIndex, match.index));
        }
        frag.append(createAmountNode(variantShopCents(), match[1] || ''));
        lastIndex = match.index + match[0].length;
        match = re.exec(source);
      }
    }

    if (lastIndex === 0) {
      const plusRe = /(^|[^\d])99\+/g;
      let plusMatch = plusRe.exec(source);
      let plusLast = 0;
      while (plusMatch) {
        const start = plusMatch.index + plusMatch[1].length;
        if (start > plusLast) frag.append(source.slice(plusLast, start));
        frag.append(createAmountNode(variantShopCents(), '+'));
        plusLast = plusMatch.index + plusMatch[0].length;
        plusMatch = plusRe.exec(source);
      }
      if (plusLast === 0) {
        frag.append(source);
      } else if (plusLast < source.length) {
        frag.append(source.slice(plusLast));
      }
      return frag;
    }

    if (lastIndex < source.length) frag.append(source.slice(lastIndex));
    return frag;
  };

  const restoreCopyWrappers = () => {
    document.querySelectorAll('[data-ab-fs-copy]').forEach((wrap) => {
      const original = copyOriginals.get(wrap);
      if (original == null) return;
      wrap.replaceWith(document.createTextNode(original));
    });
  };

  const walkCopy = (root, active) => {
    if (!root) return;

    if (!active) {
      restoreCopyWrappers();
      return;
    }

    restoreCopyWrappers();

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
        if (parent.closest(PRICE_SELECTOR)) return NodeFilter.FILTER_REJECT;
        if (parent.closest('[data-ab-fs-copy], [data-ab-fs-money]')) return NodeFilter.FILTER_REJECT;
        const contextEl = parent.closest(
          'p, li, h1, h2, h3, h4, h5, a, button, figcaption, td, th, label, .why-item-text, .why-item-title, .why-item, .feature-item, .announcement__slide'
        );
        const context = (contextEl || parent).textContent || '';
        if (!isFreeShippingContext(context) && !isFreeShippingContext(node.nodeValue || '')) {
          return NodeFilter.FILTER_REJECT;
        }
        if (!hasCopyAmount(node.nodeValue || '')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    let current = walker.nextNode();
    while (current) {
      nodes.push(current);
      current = walker.nextNode();
    }

    for (const node of nodes) {
      const parent = node.parentNode;
      if (!parent) continue;
      const source = node.nodeValue || '';
      const wrap = document.createElement('span');
      wrap.style.display = 'contents';
      wrap.dataset.abFsCopy = 'true';
      wrap.append(fragmentFromCopy(source));
      copyOriginals.set(wrap, source);
      parent.replaceChild(wrap, node);
      wrap.closest('.trans-money')?.classList.remove('trans-money');
    }
  };

  const patchCartItemsLimit = (active) => {
    const dollars = active ? VARIANT_LIMIT : themeLimitDollars();
    document.querySelectorAll('cart-items').forEach((cart) => {
      if (typeof cart.freeShippingLimit === 'undefined') return;
      cart.freeShippingLimit = dollars * 100;
    });
  };

  const refreshCartWidgets = () => {
    document.querySelectorAll('cart-items').forEach((cart) => {
      cart.updateProgress?.();
      cart.freeShippingMessageHandle?.(cart.subtotal);
    });
  };

  const setAmountContent = (el, shopCents) => {
    if (!el) return;
    el.classList.remove('trans-money');
    el.replaceChildren(createAmountNode(shopCents));
    el.closest('.trans-money')?.classList.remove('trans-money');
  };

  // Swap only the number inside a control label ("$99.00 USD") so both arms
  // keep the theme's own symbol, decimals and currency suffix.
  const formatLikeTemplate = (template, shopCents) => {
    const token = String(template || '').match(/\d[\d.,]*/)?.[0];
    if (!token) return null;
    const [whole, fraction] = (Math.max(0, Number(shopCents) || 0) / 100).toFixed(2).split('.');
    const decimalSep = /[.,]\d{2}$/.test(token) ? token.slice(-3, -2) : '.';
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, decimalSep === ',' ? '.' : ',');
    const showFraction = /[.,]\d{2}$/.test(token) || fraction !== '00';
    return template.replace(token, showFraction ? `${grouped}${decimalSep}${fraction}` : grouped);
  };

  const originalProgressText = (item, selector) => {
    const template = document.createElement('template');
    template.innerHTML = htmlOriginals.get(item) || '';
    return template.content.querySelector(selector)?.textContent?.trim() || '';
  };

  const setTemplatedAmount = (el, shopCents, template) => {
    const text = formatLikeTemplate(template, shopCents);
    if (!el || text == null) return setAmountContent(el, shopCents);
    el.classList.remove('trans-money');
    el.textContent = text;
    el.closest('.trans-money')?.classList.remove('trans-money');
  };

  const updateCartProgress = (active) => {
    document.querySelectorAll('[data-cart-progress]').forEach((progress) => {
      const item = progress.querySelector('[data-cart-progress-free-shipping]');
      if (!item) return;

      if (!htmlOriginals.has(item)) htmlOriginals.set(item, item.innerHTML);

      if (!active) {
        item.innerHTML = htmlOriginals.get(item);
        const controlMin = progress.getAttribute('data-ab-fs-min-control');
        if (controlMin != null) progress.setAttribute('data-progress-free-shipping-min', controlMin);
        return;
      }

      if (!progress.hasAttribute('data-ab-fs-min-control')) {
        progress.setAttribute(
          'data-ab-fs-min-control',
          progress.getAttribute('data-progress-free-shipping-min') || String(CONTROL_LIMIT * 100)
        );
      }

      const limitPresentment = variantPresentmentCents();
      const cartTotal = cartSubtotalCents();
      const remainingShop = Math.max(0, variantShopCents() - cartShopCents());
      const percent = limitPresentment > 0 ? Math.min(100, Math.max(0, (cartTotal / limitPresentment) * 100)) : 0;

      progress.setAttribute('data-progress-cart-total', String(cartTotal));
      progress.setAttribute('data-progress-free-shipping-min', String(limitPresentment));

      const valueTemplate = originalProgressText(item, '.cart-progress__value span');
      const valueEl = item.querySelector('.cart-progress__value span');
      setTemplatedAmount(valueEl, variantShopCents(), valueTemplate);

      const fill = item.querySelector('.cart-progress__fill');
      if (fill) fill.style.width = `${percent}%`;

      let marker = item.querySelector('.cart-progress__marker');
      if (remainingShop <= 0) {
        marker?.remove();
        progress.setAttribute('data-progress-free-shipping-qualified', 'true');
        return;
      }

      progress.setAttribute('data-progress-free-shipping-qualified', 'false');
      if (!marker) {
        marker = document.createElement('div');
        marker.className = 'cart-progress__marker';
        marker.innerHTML =
          '<span class="cart-progress__marker-value"></span><span class="cart-progress__marker-text">for free shipping</span>';
        item.append(marker);
      }
      marker.style.left = `${percent}%`;
      setTemplatedAmount(
        marker.querySelector('.cart-progress__marker-value'),
        remainingShop,
        originalProgressText(item, '.cart-progress__marker-value') || valueTemplate
      );
    });
  };

  const apply = () => {
    const active = isActive();
    if (!active && !didApply) return;
    didApply = true;
    patchCartItemsLimit(active);
    walkCopy(document.body, active);
    refreshCartWidgets();
    updateCartProgress(active);
    if (active) {
      const remainingShop = Math.max(0, variantShopCents() - cartShopCents());
      document.querySelectorAll('[data-left-to-spend]').forEach((el) => {
        setAmountContent(el, remainingShop);
      });
    }
  };

  const applySoon = () => {
    if (!isActive() && !didApply) return;
    apply();
    requestAnimationFrame(apply);
  };

  document.addEventListener('theme:cart:change', () => {
    if (!isActive()) return;
    patchCartItemsLimit(true);
    requestAnimationFrame(apply);
  });
  document.addEventListener('theme:cart:load', applySoon);
  document.addEventListener('theme:announcement:init', applySoon);
  document.addEventListener('theme:product:added', applySoon);
  document.addEventListener('shopify:section:load', applySoon);
  window.addEventListener('load', applySoon, { once: true });

  let wasActive = isActive();
  new MutationObserver(() => {
    const next = isActive();
    if (next === wasActive) return;
    wasActive = next;
    applySoon();
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

  new MutationObserver((mutations) => {
    if (!isActive()) return;
    const totalChanged = mutations.some((mutation) => {
      const target = mutation.target.nodeType === Node.TEXT_NODE ? mutation.target.parentElement : mutation.target;
      if (target?.closest?.('[data-cart-total]')) return true;
      return Array.from(mutation.addedNodes).some(
        (node) => node.nodeType === Node.ELEMENT_NODE && (node.matches?.('[data-cart-total]') || node.querySelector?.('[data-cart-total]'))
      );
    });
    if (totalChanged) requestAnimationFrame(() => updateCartProgress(true));
  }).observe(document.body, { childList: true, characterData: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        if (isActive()) applySoon();
      },
      { once: true }
    );
  } else if (isActive()) {
    applySoon();
  }
}
